import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate } from 'framer-motion';
import { Link } from 'react-router-dom';

// ==========================================
// 1. AppPreview
// ==========================================
export function AppPreview() {
  return (
    <section className="px-6 md:px-[60px] py-20 flex flex-col md:flex-row gap-20 items-center bg-gradient-to-br from-[#111520] to-[#161B26] border-y border-[rgba(255,255,255,0.07)]">
      <div className="flex-1">
        <div className="label-tag">Your personal ops center</div>
        <h2 className="font-barlow text-[36px] md:text-[42px] font-extrabold text-white tracking-[-0.8px] leading-[1.15] mb-5">
          Everything on one<br /><span className="text-cyan">live map.</span>
        </h2>
        <p className="text-[15px] text-tactical-muted leading-[1.65] mb-9 max-w-xl">
          VeriWatch aggregates eight real-time data streams into a single, actionable view. No switching between apps. No missing alerts. Just intelligence when you need it.
        </p>
        
        <div className="flex flex-col gap-5">
          <div className="flex gap-4 items-start">
            <div className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.07)] border border-[rgba(255,255,255,0.12)] flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="6" stroke="cyan" strokeWidth="1.3"/><path d="M9 5.5v3.5l2 1" stroke="cyan" strokeWidth="1.3" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div className="text-[14px] font-medium text-white mb-1">Real-time incident map</div>
              <div className="text-[13px] text-tactical-muted leading-[1.55]">Police activity, weather alerts, offender locations, and traffic incidents — all plotted on a single live map centred on your location.</div>
            </div>
          </div>
          
          <div className="flex gap-4 items-start">
            <div className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.07)] border border-[rgba(255,255,255,0.12)] flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2l1.8 5.5H17L12 11l2 5.5L9 13l-5 3.5 2-5.5L1 7.5h6.2L9 2z" stroke="#F5A623" strokeWidth="1.3" fill="none"/></svg>
            </div>
            <div>
              <div className="text-[14px] font-medium text-white mb-1">Push-first alerts</div>
              <div className="text-[13px] text-tactical-muted leading-[1.55]">Get notified before local news reports it. Our scanner AI summarises incidents in plain English so you always understand what's happening.</div>
            </div>
          </div>
          
          <div className="flex gap-4 items-start">
            <div className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.07)] border border-[rgba(255,255,255,0.12)] flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="4" width="12" height="10" rx="1.5" stroke="#2ED573" strokeWidth="1.3"/><path d="M6 8h6M6 11h4" stroke="#2ED573" strokeWidth="1.3" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div className="text-[14px] font-medium text-white mb-1">One-tap AI briefing</div>
              <div className="text-[13px] text-tactical-muted leading-[1.55]">Ask "Is my neighbourhood safe tonight?" and get an AI-powered safety briefing synthesised from all eight live data sources.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[340px] shrink-0">
        <div className="w-[280px] mx-auto relative">
          <div className="bg-[#111520] rounded-[40px] border-2 border-[rgba(255,255,255,0.12)] p-[14px] overflow-hidden">
            <div className="w-20 h-[26px] bg-[#0A0C10] rounded-b-[14px] mx-auto mb-3 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-[#161B26]" />
            </div>
            
            <div className="bg-[#161B26] rounded-[26px] overflow-hidden">
              <div className="flex justify-between items-center px-4 pt-[10px] pb-[6px] text-[#D8E0EE]">
                <span className="text-[12px]">9:41</span>
                <span className="text-[10px] text-tactical-muted">VeriWatch</span>
                <span className="text-[10px]">●●●</span>
              </div>
              
              <div className="h-[220px] bg-[#0A0C10] mx-3 rounded-[14px] relative overflow-hidden border border-[rgba(255,255,255,0.07)]">
                <div className="absolute inset-0 opacity-60">
                  <div className="absolute inset-0 bg-[#0A0C10]" />
                  <img 
                    src="/images/tactical-map.png" 
                    alt="Tactical Map" 
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:30px_30px]" />
                </div>
                
                <div className="absolute top-[18%] left-[18%] z-10">
                  <div className="w-9 h-9 rounded-full bg-cyan/20 border border-cyan/40 backdrop-blur-sm flex items-center justify-center shadow-[0_0_15px_rgba(13,148,136,0.4)] animate-pulse">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
                  </div>
                </div>

                <div className="absolute top-[15%] right-[10%] z-10">
                  <div className="w-9 h-9 rounded-full bg-cyan/20 border border-cyan/40 backdrop-blur-sm flex items-center justify-center shadow-[0_0_15px_rgba(13,148,136,0.4)] animate-pulse [animation-delay:0.5s]">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
                  </div>
                </div>

                <div className="absolute bottom-[20%] left-[55%] z-10">
                  <div className="w-9 h-9 rounded-full bg-cyan/20 border border-cyan/40 backdrop-blur-sm flex items-center justify-center shadow-[0_0_15px_rgba(13,148,136,0.4)] animate-pulse [animation-delay:1s]">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
                  </div>
                </div>
                
                <div className="absolute bottom-[10px] right-[10px] bg-[#0A0C10]/85 rounded-md px-2 py-[3px] text-[9px] font-bold text-cyan font-barlow border border-[rgba(13,148,136,0.25)] flex items-center gap-[5px] z-20">
                  <span className="w-[5px] h-[5px] rounded-full bg-[#FF4757] animate-blink" /> LIVE
                </div>
              </div>
              
              <div className="px-3 py-[10px] flex flex-col gap-[6px]">
                <div className="bg-[#1A2030] rounded-[10px] p-[10px_12px] border border-[rgba(255,255,255,0.07)] flex items-start gap-[10px]">
                  <div className="w-[7px] h-[7px] rounded-full shrink-0 mt-[3px] bg-[#FF4757]" />
                  <div>
                    <div className="text-[11px] font-semibold text-white mb-[2px]">Police activity — 0.3 mi away</div>
                    <div className="text-[10px] text-tactical-muted">Vehicle pursuit · Oak St & Michigan Ave</div>
                  </div>
                </div>
                
                <div className="bg-[#1A2030] rounded-[10px] p-[10px_12px] border border-[rgba(255,255,255,0.07)] flex items-start gap-[10px]">
                  <div className="w-[7px] h-[7px] rounded-full shrink-0 mt-[3px] bg-[#F5A623]" />
                  <div>
                    <div className="text-[11px] font-semibold text-white mb-[2px]">Tornado watch until 9PM</div>
                    <div className="text-[10px] text-tactical-muted">NWS · Move to interior room</div>
                  </div>
                </div>
                
                <div className="bg-[#1A2030] rounded-[10px] p-[10px_12px] border border-[rgba(255,255,255,0.07)] flex items-start gap-[10px]">
                  <div className="w-[7px] h-[7px] rounded-full shrink-0 mt-[3px] bg-[#2ED573]" />
                  <div>
                    <div className="text-[11px] font-semibold text-white mb-[2px]">FDA recall in your area</div>
                    <div className="text-[10px] text-tactical-muted">Spinach brand · Listeria risk</div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-[6px] px-3 pt-2 pb-[14px]">
                <div className="bg-[#1E2535] rounded-lg p-[8px_6px] text-center border border-[rgba(255,255,255,0.07)]">
                  <div className="font-barlow text-[16px] font-extrabold text-cyan">14</div>
                  <div className="text-[9px] text-tactical-muted mt-[2px]">Alerts today</div>
                </div>
                <div className="bg-[#1E2535] rounded-lg p-[8px_6px] text-center border border-[rgba(255,255,255,0.07)]">
                  <div className="font-barlow text-[16px] font-extrabold text-cyan">2</div>
                  <div className="text-[9px] text-tactical-muted mt-[2px]">Offenders nearby</div>
                </div>
                <div className="bg-[#1E2535] rounded-lg p-[8px_6px] text-center border border-[rgba(255,255,255,0.07)]">
                  <div className="font-barlow text-[16px] font-extrabold text-cyan">HIGH</div>
                  <div className="text-[9px] text-[#F5A623] mt-[2px]">Threat level</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ==========================================
// 2. Compare
// ==========================================
const compareFeatures = [
  { name: 'Live police scanner', veri: 'Free', others: '$19.99/mo', unique: false },
  { name: 'Sex offender map', veri: 'Free', others: 'Paid Add-on', unique: false },
  { name: 'AI safety assistant', veri: 'UNIQUE', others: '✕', unique: true },
  { name: 'FDA food recall alerts', veri: 'UNIQUE', others: '✕', unique: true },
  { name: 'CDC disease alerts', veri: 'UNIQUE', others: '✕', unique: true },
  { name: 'Terrorism advisories', veri: 'UNIQUE', others: '✕', unique: true },
  { name: 'Live traffic cameras', veri: 'UNIQUE', others: '✕', unique: true },
];

const compareContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const compareCardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
  }
};

export function Compare() {
  return (
    <section className="px-6 md:px-[60px] py-16 md:py-[80px] bg-[#0A0C10] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.03)_0%,transparent_70%)]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-[80px]">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="label-tag"
          >
            The Intelligence Gap
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-barlow text-[32px] sm:text-[42px] md:text-[56px] font-extrabold text-white tracking-[-1.2px] leading-[1.05] mb-6"
          >
            VeriWatch vs. <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan via-[#2dd4bf] to-cyan">The Rest.</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-[18px] text-tactical-muted max-w-[600px] mx-auto leading-relaxed font-light"
          >
            Don't settle for partial safety. See how VeriWatch stacks up against industry incumbents.
          </motion.p>
        </div>

        <motion.div 
          variants={compareContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <motion.div variants={compareCardVariants} className="hidden md:flex bg-[#111520]/50 backdrop-blur-xl border border-white/[0.05] rounded-[32px] p-8 flex-col h-full">
            <h3 className="font-barlow font-bold text-tactical-muted text-[18px] uppercase tracking-[0.1em] mb-10">Capability</h3>
            <ul className="space-y-8 flex-1">
              {compareFeatures.map((f, i) => (
                <li key={i} className="text-white font-medium text-[15px] h-6 flex items-center">{f.name}</li>
              ))}
              <li className="text-cyan font-bold text-[15px] h-6 flex items-center pt-8 border-t border-white/[0.05]">Monthly Subscription</li>
            </ul>
          </motion.div>

          <motion.div 
            variants={compareCardVariants} 
            className="bg-[#161B26] border-2 border-[cyan]/40 rounded-[32px] p-6 md:p-8 flex flex-col h-full relative shadow-[0_0_40px_rgba(13,148,136,0.15)] md:transform md:-translate-y-4"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan text-ink text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-[0_10px_20px_rgba(13,148,136,0.3)] z-20">
              Recommended
            </div>
            <h3 className="font-barlow font-bold text-white text-[24px] md:text-[28px] mb-8 md:mb-10 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan/20 border border-cyan/30 flex items-center justify-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="3"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              </div>
              VeriWatch
            </h3>
            <ul className="space-y-6 md:space-y-8 flex-1 mb-8">
              {compareFeatures.map((f, i) => (
                <li key={i} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 text-[14px]">
                  <span className="md:hidden text-[10px] font-bold text-tactical-muted uppercase tracking-wider">{f.name}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-cyan/20 border border-cyan/30 flex items-center justify-center shrink-0">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span className={f.unique ? "text-cyan font-bold" : "text-white"}>{f.veri}</span>
                  </div>
                </li>
              ))}
              <li className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 pt-8 border-t border-white/[0.05]">
                <span className="md:hidden text-[10px] font-bold text-tactical-muted uppercase tracking-wider">Pricing</span>
                <span className="text-white font-black text-[20px] md:text-[24px] tracking-tight">
                  $0.00 <span className="text-[12px] text-tactical-muted font-bold ml-2 uppercase tracking-widest">Free Forever</span>
                </span>
              </li>
            </ul>
            <Link to="/auth" className="w-full py-4 rounded-2xl bg-cyan text-ink font-black uppercase tracking-widest text-[12px] hover:scale-[1.02] transition-transform shadow-[0_20px_40px_rgba(13,148,136,0.2)] mt-auto flex items-center justify-center">
              Access All Layers Now
            </Link>
          </motion.div>

          <motion.div variants={compareCardVariants} className="bg-[#111520] border border-white/[0.05] rounded-[32px] p-6 md:p-8 flex flex-col h-full transition-all">
            <h3 className="font-barlow font-bold text-tactical-muted text-[24px] md:text-[28px] mb-8 md:mb-10">Others</h3>
            <ul className="space-y-6 md:space-y-8 flex-1 mb-8">
              {compareFeatures.map((f, i) => (
                <li key={i} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 text-[14px]">
                  <span className="md:hidden text-[10px] font-bold text-tactical-muted uppercase tracking-wider">{f.name}</span>
                  <div className="flex items-center gap-3">
                    {f.others === '✕' ? (
                      <div className="w-5 h-5 rounded-full bg-red/10 border border-red/20 flex items-center justify-center shrink-0">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-amber/10 border border-amber/20 flex items-center justify-center shrink-0">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    )}
                    <span className="text-tactical-muted">{f.others}</span>
                  </div>
                </li>
              ))}
              <li className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 pt-8 border-t border-white/[0.05]">
                <span className="md:hidden text-[10px] font-bold text-tactical-muted uppercase tracking-wider">Pricing</span>
                <span className="text-tactical-muted font-bold text-[20px] md:text-[24px] tracking-tight">
                  $19.99 <span className="text-[12px] text-tactical-muted font-bold ml-2 uppercase tracking-widest">Per Month</span>
                </span>
              </li>
            </ul>
            <div className="w-full py-4 rounded-2xl border border-white/10 text-[#3A4558] font-bold uppercase tracking-widest text-[12px] flex items-center justify-center">
              Switch to Better
            </div>
          </motion.div>
        </motion.div>
        </div>
    </section>
  );
}

// ==========================================
// 3. Features
// ==========================================
const featuresData = [
  {
    title: 'Meet "Tank": Chief Resilience Officer',
    desc: `Former Army mechanic turned trucker, Tank has logged over 2 million miles across all 48 continental states. His 18-wheeler doubles as a mobile command center, equipped with HAM radio, solar panels, and a 6-month food supply.

After witnessing the 2021 Texas grid collapse firsthand while delivering emergency supplies, Tank became obsessed with preparedness. He now spends his off-hours monitoring OSINT feeds, running readiness drills with his local prepper network, and training VeriWatch's AI systems with real-world threat scenarios.
`,
    unique: true,
    className: 'md:col-span-2 md:row-span-2',
    icon: (
      <img 
        src="https://veriwatch.io/images/tank.png" 
        alt="Tank" 
        className="w-full h-full object-cover rounded-xl"
      />
    ),
    bg: 'bg-[radial-gradient(circle_at_bottom_right,rgba(13,148,136,0.1)_0%,transparent_60%)]'
  },
  {
    title: 'Sex offender map',
    desc: 'Nationwide registry mapped to your location. Get instant alerts near home.',
    className: 'md:col-span-1 md:row-span-1',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="cyan" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  {
    title: 'AI safety assistant',
    desc: 'Ask "Is my neighbourhood safe tonight?" Get an AI briefing from live scanner data and local incidents.',
    className: 'md:col-span-1 md:row-span-1',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="cyan" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
      </svg>
    ),
    bg: 'bg-[radial-gradient(circle_at_top_right,rgba(13,148,136,0.08)_0%,transparent_50%)]'
  },
  {
    title: 'Severe weather',
    desc: 'NWS-powered push notifications for tornadoes and hurricanes.',
    status: 'urgent',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 16.326A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.973"/>
        <path d="m13 12-3 5h4l-3 5"/>
      </svg>
    ),
  },
  {
    title: 'Traffic cameras',
    desc: 'Access live DOT cameras. See road conditions in real time.',
    className: 'md:col-span-1 md:row-span-1',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="cyan" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/>
      </svg>
    ),
  },
  {
    title: 'CDC disease alerts',
    desc: 'Real-time outbreak notifications from the CDC.',
    className: 'md:col-span-2 md:row-span-1',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="cyan" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="M2 12h2"/><path d="m4.93 19.07 1.41-1.41"/><path d="M12 22v-2"/><path d="m19.07 19.07-1.41-1.41"/><path d="M22 12h-2"/><path d="m19.07 4.93-1.41 1.41"/><circle cx="12" cy="12" r="4"/>
      </svg>
    ),
  },
  {
    title: 'FDA food recalls',
    desc: 'Instant alerts for food and drug recalls.',
    className: 'md:col-span-1 md:row-span-1',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="cyan" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
    ),
  },
  {
    title: 'Terrorism advisories',
    desc: 'DHS threat level monitoring and advisories.',
    status: 'urgent',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
];

const featuresContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const featuresItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    },
  },
};

export function Features() {
  return (
    <section className="px-6 md:px-[60px] py-16 md:py-[20px] md:mt-20">
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="text-center mb-[60px]"
      >
        <motion.div variants={featuresItemVariants} id="features" className="label-tag">Everything in one app</motion.div>
        <motion.h2 variants={featuresItemVariants} className="font-barlow text-[32px] sm:text-[36px] md:text-[44px] font-extrabold text-white tracking-[-0.8px] leading-[1.15] mb-[14px]">
          One app. <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan via-[#2dd4bf] to-cyan">Eight intelligence layers.</span>
        </motion.h2>
        <motion.p variants={featuresItemVariants} className="text-[16px] text-tactical-muted max-w-[480px] mx-auto">
          The only platform that gives everyday people access to the same real-time data as law enforcement and emergency responders.
        </motion.p>
      </motion.div>

      <motion.div 
        variants={featuresContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[minmax(180px,auto)] gap-4"
      >
        {featuresData.map((feat, idx) => (
          <motion.div 
            key={idx} 
            variants={featuresItemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className={`group bg-[#1A2030] border border-[rgba(255,255,255,0.07)] rounded-[24px] p-8 transition-all duration-300 hover:border-[rgba(13,148,136,0.3)] relative overflow-hidden flex flex-col justify-between ${feat.className || ''} ${feat.bg || ''}`}
          >
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-2xl bg-[rgba(13,148,136,0.12)] border border-[rgba(13,148,136,0.2)] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {feat.icon}
              </div>
              
              <h3 className={`font-barlow font-bold text-white mb-3 tracking-[-0.3px] ${feat.className?.includes('col-span-2') ? 'text-[24px]' : 'text-[18px]'}`}>
                {feat.title}
              </h3>
              <p className={`text-tactical-muted leading-[1.6] whitespace-pre-line ${feat.className?.includes('col-span-2') ? 'text-[15px] max-w-sm' : 'text-[13px]'}`}>
                {feat.desc}
              </p>
            </div>

            {feat.unique && (
              <div className="absolute top-6 right-6 flex flex-col items-end gap-2 z-20">
                <span className="inline-block text-[12px] font-black font-barlow tracking-[0.15em] uppercase px-3 py-1.5 rounded-lg bg-[rgba(13,148,136,0.15)] text-cyan border border-[rgba(13,148,136,0.25)] shadow-[0_4px_12px_rgba(0,0,0,0.1)] backdrop-blur-sm">
                   Only on VeriWatch
                </span>
                {feat.status === 'urgent' && (
                  <span className="inline-flex items-center gap-1.5 text-[8px] font-black font-barlow tracking-[0.15em] uppercase px-3 py-1.5 rounded-lg bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/25 shadow-[0_4px_12px_rgba(0,0,0,0.1)] backdrop-blur-sm animate-pulse">
                    <span className="w-1 h-1 rounded-full bg-[#F59E0B]" />
                    Live
                  </span>
                )}
              </div>
            )}

            {!feat.unique && feat.status === 'urgent' && (
               <div className="absolute top-6 right-6 z-20">
                  <span className="inline-flex items-center gap-1.5 text-[8px] font-black font-barlow tracking-[0.15em] uppercase px-3 py-1.5 rounded-lg bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/25 shadow-[0_4px_12px_rgba(0,0,0,0.1)] backdrop-blur-sm">
                    <span className="w-1 h-1 rounded-full bg-[#F59E0B] animate-ping" />
                    Alert
                  </span>
               </div>
            )}

            {feat.className?.includes('col-span-2') && (
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:20px_20px] [mask-image:radial-gradient(ellipse_at_bottom_right,black,transparent)] pointer-events-none" />
            )}
          </motion.div>
        ))}
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
        className="mt-16 text-center"
      >
        <p className="text-tactical-muted text-sm mb-6 font-medium tracking-wide">Ready to access the ultimate safety toolkit?</p>
        <Link 
          to="/auth" 
          className="inline-flex items-center justify-center gap-3 bg-cyan text-[#0A0C10] text-[15px] font-bold px-10 py-4 rounded-full font-barlow hover:brightness-110 hover:scale-105 transition-all shadow-[0_20px_40px_rgba(13,148,136,0.2)] group w-full sm:w-auto"
        >
          Explore All Features
          <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
        </Link>
      </motion.div>
    </section>
  );
}

// ==========================================
// 4. FinalCTA
// ==========================================
export function FinalCTA() {
  return (
    <section className="px-6 md:px-[60px] py-16 md:py-[80px] text-center relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[radial-gradient(ellipse,rgba(13,148,136,0.07)_0%,transparent_65%)] pointer-events-none" />
      
      <div className="label-tag">Ready to know everything?</div>
      <h2 className="font-barlow text-[32px] sm:text-[40px] md:text-[52px] font-extrabold text-white leading-[1.1] tracking-[-1px] mb-[18px]">
        Your neighbourhood.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan via-[#2dd4bf] to-cyan">Fully visible.</span> Always free.
      </h2>
      <p className="text-[16px] text-tactical-muted mb-10 max-w-lg mx-auto">
        Join 500,000+ Americans who never miss a safety alert. Download VeriWatch in 30 seconds.
      </p>

      <div className="flex flex-col items-center gap-6 mb-10">
        <Link to="/auth" className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-cyan text-ink text-[15px] font-black px-12 py-5 rounded-2xl transition-all duration-300 hover:scale-[1.05] hover:shadow-[0_20px_40px_rgba(13,148,136,0.4)] overflow-hidden tracking-widest uppercase">
          <span className="relative z-10">Get Your Free Account</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 group-hover:translate-x-1 transition-transform"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </Link>
      </div>
      
      <div className="text-[12px] text-tactical-muted flex flex-wrap justify-center gap-x-3 gap-y-1">
        <span>No credit card</span>
        <span className="text-[#3A4558]">·</span>
        <span>No ads</span>
        <span className="text-[#3A4558]">·</span>
        <span>Forever free tier</span>
      </div>
    </section>
  );
}

// ==========================================
// 5. Footer
// ==========================================
export function Footer() {
  return (
    <>
    <footer className="border-t border-[rgba(255,255,255,0.07)] px-6  py-10 flex flex-col md:flex-row justify-between items-center gap-8 md:gap-4">
      <div className="flex items-center justify-center">
        <img 
          src="https://veriwatch.io/veriwatch-logo.png" 
          alt="VeriWatch Logo" 
          width={100} 
          height={24} 
          style={{ width: 'auto' }}
          className="h-6 brightness-90 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
        />
      </div>
      <div className='flex gap-[24px] justify-center'>
      <div className="flex items-center justify-center gap-5">
        <a href="https://x.com/VeriWatch" target="_blank" rel="noopener noreferrer" className="text-tactical-muted hover:text-white transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
        <a href="https://www.facebook.com/profile.php?id=61574512000742" target="_blank" rel="noopener noreferrer" className="text-tactical-muted hover:text-white transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
        <a href="https://www.instagram.com/veri_watch/" target="_blank" rel="noopener noreferrer" className="text-tactical-muted hover:text-white transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
        </a>
        <a href="https://www.linkedin.com/company/veriwatch/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-tactical-muted hover:text-white transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
        </a>
      </div>
      </div>
    </footer>
    <div className="text-[12px] text-neutral-400 text-center mb-4">© 2026 VeriWatch Inc. All rights reserved.</div>
    </>
  );
}

// ==========================================
// 6. Hero
// ==========================================
export function Hero() {
  return (
    <>
    <section className="min-h-[80vh] lg:h-[80vh] flex flex-col lg:flex-row items-center justify-between px-6 lg:px-[60px] py-20 lg:py-24 relative overflow-hidden bg-[#0A0C10]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15)_0%,transparent_70%)] blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.08)_0%,transparent_70%)] blur-[120px]" />
        <div className="absolute top-[20%] right-[10%] w-[40%] h-[40%] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.1)_0%,transparent_70%)] blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:50px_50px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_80%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_2px_2px,rgba(255,255,255,0.05)_1px,transparent_0)] bg-[length:50px_50px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_90%)]" />
      </div>

      <div className="max-w-[1440px] mx-auto w-full flex flex-col lg:flex-row items-center justify-between relative z-10">
        <div className="flex-1 text-left w-full max-w-2xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[0.95] tracking-tight text-white mb-8 animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-150">
            Own Your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan via-[#2dd4bf] to-cyan">Neighborhood.</span>
          </h1>

          <p className="text-lg md:text-xl text-tactical-muted leading-relaxed max-w-lg mb-12 font-light animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
            The only platform aggregating <b className="text-white font-medium">Government Data real-time intelligence layers</b> into one single view. Police scanner, weather, offender maps, and AI briefings.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-5 mb-16 animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-500">
          <Link to="/auth" className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-cyan text-ink text-[13px] font-black px-12 py-5 rounded-2xl transition-all duration-300 hover:scale-[1.05] hover:shadow-[0_20px_40px_rgba(13,148,136,0.4)] overflow-hidden tracking-widest uppercase">
              <span className="relative z-10">Enter Command Center Now</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 group-hover:translate-x-1 transition-transform"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8 animate-in fade-in duration-1000 delay-700">
            <div className="flex -space-x-4">
              {[1, 2, 3, 4].map((img) => (
                <div key={img} className="w-12 h-12 rounded-full border-4 border-ink overflow-hidden bg-surface">
                  <img src={`https://i.pravatar.cc/100?img=${img + 10}`} alt="User" className="w-full h-full object-cover" />
                </div>
              ))}
              <div className="w-12 h-12 rounded-full border-4 border-ink bg-cyan flex items-center justify-center text-[11px] font-black text-ink z-10">
                500K
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/10" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <svg key={i} className="w-4 h-4 text-cyan fill-cyan" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                ))}
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-tactical-muted">Trusted by 500k+ Families</span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center relative w-full mt-16 lg:mt-0 lg:ml-12 perspective-[2000px]">
          <div className="relative w-full max-w-[600px] aspect-[4/3] ">
            <div className="absolute inset-0 bg-surface/40 backdrop-blur-3xl border border-white/10 rounded-[2.5rem] shadow-[0_40px_80px_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.05)] overflow-hidden">
              <div className="h-16 border-b border-white/5 flex items-center px-8 justify-between bg-white/[0.02]">
                <div className="flex items-center gap-4">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green/80" />
                  </div>
                  <div className="w-px h-4 bg-white/10 mx-2" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-tactical-muted">Tactical View v4.2</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-red/10 border border-red/20">
                  <div className="w-1.5 h-1.5 rounded-full bg-red animate-pulse" />
                  <span className="text-[9px] font-bold uppercase text-red">8 Active Alerts</span>
                </div>
              </div>

              <div className="p-8 h-[calc(100%-4rem)] flex flex-col gap-6">
                <div className="flex-1 flex gap-6">
                  <div className="flex-[2] rounded-3xl bg-ink/80 border border-white/5 relative overflow-hidden group">
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(13,148,136,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(13,148,136,0.1)_1px,transparent_1px)] bg-[length:40px_40px] opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan/5 to-transparent w-[50%] h-full -skew-x-12 translate-x-[-100%] animate-[scan_4s_linear_infinite]" />
                    <div className="absolute top-[12%] right-[34.5%]">
                      <div className="w-4 h-4 bg-[#AB1810] rounded-full relative z-10 animate-blink shadow-[0_0_20px_rgba(255,71,87,1)]" />
                      <div className="w-32 h-32 bg-[#AB1810] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-ring" />
                    </div>
                    <div className="absolute top-[60%] left-[70%]">
                      <div className="w-3 h-3 bg-cyan rounded-full relative z-10 shadow-[0_0_15px_rgba(13,148,136,1)]" />
                    </div>
                    <div className="absolute top-[20%] left-[80%] opacity-40">
                      <div className="w-2 h-2 bg-amber rounded-full relative z-10" />
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center bg-[#001626] backdrop-blur-[2px]">
                      <img 
                        src="https://veriwatch.io/veriwatch-logo.png" 
                        alt="VeriWatch-Logo" 
                        className="relative z-20 w-48 lg:w-64 h-auto object-contain mt-[-3rem]"
                      />
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col gap-4">
                    <div className="flex-1 rounded-2xl bg-surface/30 border border-white/5 p-4 flex flex-col justify-center gap-2">
                      <span className="text-[9px] font-bold uppercase text-tactical-muted tracking-wider">Neighborhood Score</span>
                      <div className="flex items-end gap-2">
                        <span className="text-3xl font-bold text-white tracking-tighter">98.4</span>
                        <span className="text-[10px] font-bold text-green mb-1.5">+2.4%</span>
                      </div>
                    </div>
                    <div className="flex-1 rounded-2xl bg-surface/30 border border-white/5 p-4 flex flex-col justify-center gap-2">
                      <span className="text-[9px] font-bold uppercase text-tactical-muted tracking-wider">Intelligence Layers</span>
                      <div className="flex items-end gap-2">
                        <span className="text-3xl font-bold text-white tracking-tighter">08</span>
                        <div className="flex gap-0.5 mb-2">
                          {[1,2,3].map(i => <div key={i} className="w-1 h-3 bg-cyan rounded-full" />)}
                          <div className="w-1 h-3 bg-white/10 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-32 rounded-3xl bg-ink/40 border border-white/5 p-6 flex items-center gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-cyan/10 border border-cyan/20 flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="text-xs font-bold text-white">Area Intelligence Update</span>
                      <span className="text-[10px] font-medium text-tactical-muted">2 mins ago</span>
                    </div>
                    <p className="text-[13px] text-tactical-muted leading-relaxed">No critical incidents reported in your 5-mile radius. Local scanner is currently clear.</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[10px] font-black uppercase text-green">Secured</span>
                    <div className="w-12 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="w-full h-full bg-green rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -right-8 bottom-[10%] w-56 p-5 rounded-2xl bg-surface/60 backdrop-blur-xl border border-white/10 shadow-2xl [animation-delay:-4s]">
               <div className="flex items-center gap-4 mb-3">
                 <div className="w-10 h-10 rounded-full border-2 border-cyan/30 flex items-center justify-center text-[10px] font-bold text-cyan">
                   AI
                 </div>
                 <div className="flex flex-col">
                   <span className="text-xs font-bold text-white">Intelligence Brief</span>
                   <span className="text-[9px] text-tactical-muted uppercase">Ready to view</span>
                 </div>
               </div>
               <Link to="/auth" className="w-full inline-flex items-center justify-center py-2 rounded-lg bg-cyan/10 border border-cyan/20 text-[10px] font-bold text-cyan uppercase tracking-widest hover:bg-cyan/20 transition-all">
                 Open Report
               </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div className="relative z-20 mt-10">
      <Ticker />
    </div>
    </>
  );
}

// ==========================================
// 7. Navbar
// ==========================================
export function Navbar() {
  return (
    <nav className="w-full bg-[#021626] backdrop-blur-xl mt-2 rounded-full border-bottom border-[rgba(255,255,255,0.07)] px-6 md:px-[60px] h-16 flex items-center justify-between sticky top-2 z-50">
      <Link to="/" className="flex items-center">
        <img 
          src="https://veriwatch.io/veriwatch-logo.png" 
          alt="VeriWatch Logo" 
          width={120} 
          height={32} 
          style={{ width: 'auto' }}
          className="h-12 md:h-14 lg:h-16"
        />
      </Link>
      <div className="hidden md:flex gap-8">
        <Link to="/" className="text-sm text-tactical-muted hover:text-white transition-colors">Home</Link>
        <a href="#features" className="text-sm text-tactical-muted hover:text-white transition-colors">Features</a>
        <Link to="/pricing" className="text-sm text-tactical-muted hover:text-white transition-colors">Pricing</Link>
      </div>
      <div className="flex items-center gap-6">
        <Link to="/auth" className="hidden sm:block text-sm font-semibold text-tactical-muted hover:text-white transition-colors">Sign In</Link>
        <Link to="/auth" className="bg-[#0D9488] text-[#0A0C10] text-[13px] font-bold px-6 py-[10px] rounded-full font-barlow hover:brightness-110 transition-all">
          Get started
        </Link>
      </div>
    </nav>
  );
}

// ==========================================
// 8. SocialProof
// ==========================================
const socialStats = [
  { n: '50', l: 'States covered' },
  { n: '32+', l: 'Live scanner feeds' },
  { n: '15,000+', l: 'Traffic cameras' },
];

const reviews = [
  {
    text: "I had no idea there was a registered sex offender two blocks from my daughter's school until VeriWatch told me. I check this app every single morning now. Worth every penny.",
    author: "Sarah M.",
    location: "Columbus, OH · Parent of 3",
    initials: "SM",
  },
  {
    text: "Replaced four apps with one. The live scanner feature alone is worth downloading. The AI assistant is wild — it summarises scanner chatter into plain English I can actually understand.",
    author: "Marcus T.",
    location: "Chicago, IL · Daily commuter",
    initials: "MT",
    highlight: true,
  },
  {
    text: "As a neighbourhood watch volunteer I've been using a physical scanner for 12 years. VeriWatch is everything I had, plus weather, plus food recalls, plus an AI I can actually ask questions to.",
    author: "Diane W.",
    location: "Scottsdale, AZ · Watch captain",
    initials: "DW",
  },
];

const socialContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const socialItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export function SocialProof() {
  return (
    <section className="px-6 md:px-[60px] py-16 md:py-[80px] text-center bg-[#0A0C10]">
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="label-tag mb-12"
      >
        Intelligence for the masses
      </motion.div>
      
      <motion.div 
        variants={socialContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-[60px] mb-[60px] md:mb-[100px] max-w-5xl mx-auto"
      >
        {socialStats.map((stat, idx) => (
          <motion.div key={idx} variants={socialItemVariants} className="text-center group">
            <div className="font-barlow text-[32px] sm:text-[42px] md:text-[60px] font-extrabold text-[#0D9488] leading-none group-hover:scale-110 transition-transform duration-500">{stat.n}</div>
            <div className="text-[10px] md:text-[14px] font-bold uppercase tracking-widest text-tactical-muted mt-4 group-hover:text-white transition-colors">{stat.l}</div>
          </motion.div>
        ))}
        <div className="col-span-full text-center -mt-8 md:-mt-8">
          <span className="text-[14px] md:text-[16px] font-bold text-[#F59E0B] uppercase tracking-[0.2em] opacity-80">— All in One App —</span>
        </div>
      </motion.div>
      

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        {reviews.map((rev, idx) => (
          <motion.div 
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1, duration: 0.8 }}
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            className={`bg-[#111520] border rounded-[28px] p-8 text-left transition-all duration-500 relative overflow-hidden group ${rev.highlight ? 'border-[#0D9488]/30 shadow-[0_20px_40px_rgba(13,148,136,0.1)]' : 'border-white/[0.05] hover:border-[#0D9488]/20'}`}
          >
            {rev.highlight && (
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#0D9488]/5 blur-3xl" />
            )}

            <div className="flex gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <svg key={i} className="w-4 h-4 text-amber fill-amber" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              ))}
            </div>
            
            <p className="text-[15px] text-[#D8E0EE] leading-[1.8] italic mb-8 relative z-10 group-hover:text-white transition-colors">"{rev.text}"</p>
            
            <div className="flex items-center gap-4 relative z-10 pt-6 border-t border-white/[0.05]">
              <div className="w-10 h-10 rounded-full bg-cyan/10 border border-cyan/20 flex items-center justify-center text-[12px] font-bold text-cyan font-barlow shrink-0 group-hover:bg-cyan group-hover:text-ink transition-all">
                {rev.initials}
              </div>
              <div className="flex flex-col">
                <span className="text-[14px] text-white font-bold tracking-tight">{rev.author}</span>
                <span className="text-[12px] text-tactical-muted uppercase font-bold tracking-widest">{rev.location}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ==========================================
// 9. Ticker
// ==========================================
const tickerItems = [
  { type: 'Police', city: 'Chicago, IL', msg: 'Structure fire near Wicker Park, 3 units responding', color: 'var(--red)' },
  { type: 'Weather', city: 'Austin, TX', msg: 'Tornado watch issued until 10PM CDT', color: 'var(--amber)', badgeClass: 'text-[#F5A623] bg-[rgba(245,166,35,0.12)]' },
  { type: 'FDA Recall', city: 'Nationwide', msg: 'Listeria contamination: 4 produce brands recalled', color: 'var(--green)', badgeClass: 'text-[#2ED573] bg-[rgba(46,213,115,0.1)]' },
  { type: 'Scanner', city: 'Denver, CO', msg: 'I-25 northbound closure, major accident mile 218', color: 'var(--cyan)', badgeClass: 'text-[#0D9488] bg-[rgba(13,148,136,0.15)]' },
  { type: 'Sex Offender', city: 'Phoenix, AZ', msg: 'New registration within 0.5mi of Arcadia Elementary', color: 'var(--red)' },
  { type: 'Weather', city: 'Tampa, FL', msg: 'Hurricane watch: Category 2 landfall expected in 18hrs', color: 'var(--amber)', badgeClass: 'text-[#F5A623] bg-[rgba(245,166,35,0.12)]' },
  { type: 'CDC Alert', city: 'Southeast US', msg: 'Elevated flu activity reported in 6 states', color: 'var(--green)', badgeClass: 'text-[#2ED573] bg-[rgba(46,213,115,0.1)]' },
  { type: 'Scanner', city: 'Los Angeles, CA', msg: 'Vehicle pursuit on I-10, LAPD involved', color: 'var(--cyan)', badgeClass: 'text-[#0D9488] bg-[rgba(13,148,136,0.15)]' },
];

export function Ticker() {
  const allItems = [...tickerItems, ...tickerItems]; 

  return (
    <div className="border-y border-[rgba(255,255,255,0.07)] bg-[#161B26]/70 overflow-hidden whitespace-nowrap">
      <div className="inline-flex py-3 animate-ticker">
        {allItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center gap-[10px] px-8 border-r border-[rgba(255,255,255,0.07)]">
            <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: item.color }} />
            <span className={`text-[9px] font-bold font-barlow tracking-[0.08em] px-[7px] py-[2px] rounded-full uppercase ${item.badgeClass || 'text-[#FF4757] bg-[rgba(255,71,87,0.12)]'}`}>
              {item.type}
            </span>
            <span className="text-sm text-[#D8E0EE]">{item.city}</span>
            <span className="text-sm text-tactical-muted"> — {item.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 10. WhyDifferent
// ==========================================
const whyItems = [
  {
    title: "All-in-one platform",
    desc: "No more switching between 10 different apps. Everything you need for safety in one tactical view.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="7" height="7" x="3" y="3" rx="1" />
        <rect width="7" height="7" x="14" y="3" rx="1" />
        <rect width="7" height="7" x="14" y="14" rx="1" />
        <rect width="7" height="7" x="3" y="14" rx="1" />
      </svg>
    )
  },
  {
    title: "Proactive alerts",
    desc: "Don't wait for the news. Get notified BEFORE something affects you with our low-latency alert engine.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    )
  },
  {
    title: "Affordable",
    desc: "Starting at just $12/month (less than a tank of gas). Premium safety shouldn't be a luxury.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/>
      </svg>
    )
  },
  {
    title: "No fear-mongering",
    desc: "Information, not paranoia. We provide cold, hard data so you can make rational decisions for your family.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    )
  }
];

function WhyCard({ item, index }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [7, -7]), { damping: 20, stiffness: 200 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-7, 7]), { damping: 20, stiffness: 200 });

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    mouseX.set((clientX - left) / width - 0.5);
    mouseY.set((clientY - top) / height - 0.5);
  }

  function handleMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
  }

  const background = useMotionTemplate`
    radial-gradient(
      350px circle at ${useTransform(mouseX, [-0.5, 0.5], ["0%", "100%"])} ${useTransform(mouseY, [-0.5, 0.5], ["0%", "100%"])},
      rgba(13, 148, 136, 0.1),
      transparent 80%
    )
  `;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateX: 5 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 1, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: "1000px",
        rotateX,
        rotateY,
      }}
      className="group relative h-full rounded-[32px] bg-[#111520]/50 backdrop-blur-xl border border-white/[0.05] p-10 transition-colors duration-500 hover:border-cyan/30 hover:bg-[#161B26]"
    >
      <motion.div
        className="pointer-events-none absolute inset-0 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background }}
      />

      <div className="absolute inset-0 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
         <div className="absolute inset-0 rounded-[32px] border border-cyan/10 shadow-[inset_0_0_20px_rgba(13,148,136,0.05)]" />
      </div>

      <motion.div 
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
        className="w-16 h-16 rounded-[22px] bg-cyan/5 border border-cyan/10 flex items-center justify-center mb-10 text-cyan group-hover:text-white group-hover:bg-cyan/20 group-hover:border-cyan/30 group-hover:shadow-[0_0_30px_rgba(13,148,136,0.3)] transition-all duration-500 relative z-10"
      >
        {item.icon}
      </motion.div>

      <h3 className="font-barlow font-bold text-white text-[26px] mb-4 tracking-[-0.6px] relative z-10 group-hover:translate-x-1 transition-transform duration-500">
        {item.title}
      </h3>
      
      <p className="text-tactical-muted text-[15px] leading-[1.7] group-hover:text-white/80 transition-colors duration-500 relative z-10 flex-grow">
        {item.desc}
      </p>

      <div className="mt-10 flex items-center gap-4 relative z-10">
        <div className="h-[1px] flex-grow bg-white/[0.05] group-hover:bg-cyan/30 transition-all duration-700 origin-left scale-x-100" />
        <motion.div 
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-2 h-2 rounded-full bg-cyan/20 group-hover:bg-cyan group-hover:shadow-[0_0_10px_rgba(13,148,136,1)] transition-all" 
        />
      </div>

      <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-100 transition-all duration-700 translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(13, 148, 136, 0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 7h10v10"/><path d="M7 17 17 7"/>
        </svg>
      </div>
    </motion.div>
  );
}

export function WhyDifferent() {
  return (
    <section className="px-6 md:px-[60px] py-20 md:py-[60px] bg-[#0A0C10] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay" 
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} 
      />

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-cyan/10 rounded-full blur-[160px] animate-pulse-slow" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-cyan/5 rounded-full blur-[140px]" />
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-[60px] md:mb-[100px]">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="label-tag mb-6"
          >
            Tactical Edge
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-barlow text-[32px] sm:text-[36px] md:text-[44px] font-extrabold text-white tracking-[-1.5px] leading-[0.95] mb-8"
          >
            Engineering <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan via-[#2dd4bf] to-cyan">Resilience.</span>
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-[19px] text-tactical-muted max-w-[680px] mx-auto leading-relaxed font-light"
          >
            VeriWatch is the first intelligence platform built for citizens, by experts. We prioritize clarity, speed, and uncompromising reliability.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {whyItems.map((item, idx) => (
            <WhyCard key={idx} item={item} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

// LandingPage Main Component
export default function LandingPage() {
  return (
    <div className="bg-[#0A0C10] min-h-screen text-white selection:bg-cyan/30 selection:text-cyan">
      <Navbar />
      <Hero />
      <SocialProof />
      <AppPreview />
      <Features />
      <WhyDifferent />
      <Compare />
      <FinalCTA />
      <Footer />
    </div>
  );
}
